import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { ColumnDef, ColumnFiltersState, VisibilityState, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared-kernel/primary/shared/shadcn/ui/table";
import { DataTablePagination } from "@shared-kernel/primary/shared/shadcn/ui/data-table-with-pagination";
import { DataTableColumnHeader } from "./data-table-column-header";
import { DataTableFilter, DataTableToolbar } from "@shared-kernel/primary/shared/shadcn/ui/data-table-toolbar";
import { DataTableSkeletonRows } from "./data-table-loading";

interface ServerDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  pageCount: number;
  pageIndex: number;
  searchPlaceHolder?: string;
  displayDataViewOptions?: boolean;
  pageSize?: number;
  invisibleColumns?: VisibilityState;
  filters?: DataTableFilter[];
  toolbarChildren?: React.ReactNode;
  sortField?: string;
  order?: "asc" | "desc";
  onPaginationChange: (pageIndex: number, pageSize: number) => void;
  onSortingChange: (sorting: SortingState) => void;
  onGlobalFilterChange?: (value: string) => void;
  onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
  searchTerm: string;
  isLoading: boolean;
  // Optional function to get navigation URL for a row
  // Return undefined to make specific rows non-clickable
  navigateTo?: (row: TData) => string | undefined;
}

export function ServerDataTable<TData, TValue>({
  columns,
  data,
  pageCount,
  pageIndex,
  searchPlaceHolder,
  displayDataViewOptions = false,
  pageSize = 10,
  invisibleColumns = {},
  filters,
  toolbarChildren,
  sortField,
  order,
  onPaginationChange,
  onSortingChange,
  onGlobalFilterChange,
  onColumnFiltersChange,
  searchTerm,
  isLoading,
  navigateTo,
}: ServerDataTableProps<TData, TValue>) {
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(invisibleColumns);
  const sorting = sortField ? [{ id: sortField, desc: order === "desc" }] : [];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount,
    onColumnFiltersChange: updater => {
      const nextFilters = typeof updater === "function" ? updater(columnFilters) : updater;
      setColumnFilters(nextFilters);
      onColumnFiltersChange?.(nextFilters);
    },
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: value => {
      if (onGlobalFilterChange && value !== searchTerm) {
        onGlobalFilterChange(String(value));
      }
    },
    onPaginationChange: updater => {
      if (typeof updater === "function") {
        const nextState = updater({
          pageIndex,
          pageSize,
        });
        onPaginationChange(nextState.pageIndex, nextState.pageSize);
      } else {
        onPaginationChange(updater.pageIndex, updater.pageSize);
      }
    },
    onSortingChange: updater => {
      const nextState = typeof updater === "function" ? updater(sorting) : updater;
      onSortingChange(nextState);
    },
    autoResetPageIndex: false, // Prevent automatic page reset when data changes
    state: {
      columnFilters,
      columnVisibility,
      globalFilter: searchTerm,
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
    },
  });

  return (
    <div className="w-full">
      <DataTableToolbar
        table={table}
        displayDataViewOptions={displayDataViewOptions}
        searchPlaceHolder={searchPlaceHolder}
        globalFilter={table.getState().globalFilter}
        setGlobalFilter={value => {
          table.setGlobalFilter(value);
        }}
        filters={filters}
      >
        {toolbarChildren}
      </DataTableToolbar>
      <div className="rounded-md border p-1">
        <Table style={{ tableLayout: "fixed" }}>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const { meta } = header.column.columnDef;
                  const { width } = meta ?? { width: null };
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        width: width ? width : "auto",
                        wordWrap: "break-word",
                        whiteSpace: "normal",
                      }}
                    >
                      {header.column.columnDef.enableSorting ? (
                        <DataTableColumnHeader
                          column={header.column}
                          title={header.column.columnDef.meta?.title ?? "Add a meta property to your column"}
                          displayHide={displayDataViewOptions}
                        />
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <DataTableSkeletonRows columnCount={table.getVisibleFlatColumns().length} pageSize={pageSize} />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => {
                const rowUrl = navigateTo ? navigateTo(row.original) : undefined;
                const isRowClickable = !!rowUrl;

                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={isRowClickable ? "cursor-pointer hover:bg-accent/50" : undefined}
                    onClick={(event: React.MouseEvent) => {
                      if (!navigateTo || !rowUrl) return;

                      // Check if the clicked element is an interactive element
                      const target = event.target as HTMLElement;
                      const isInteractiveElement = target.closest(
                        'button, input, select, textarea, a, [role="button"], [role="checkbox"], [role="switch"]',
                      );

                      if (!isInteractiveElement) {
                        const isModifierPressed = event.metaKey || event.ctrlKey;

                        if (isModifierPressed) {
                          // Open in new tab for Cmd+click (Mac) or Ctrl+click (Windows/Linux)
                          window.open(window.location.origin + rowUrl, "_blank", "noopener,noreferrer");
                        } else {
                          // Normal navigation
                          navigate(rowUrl);
                        }
                      }
                    }}
                  >
                    {row.getVisibleCells().map(cell => {
                      const { meta } = cell.column.columnDef;
                      let className;
                      const hasMeta = cell.getContext().cell.column.columnDef.meta;
                      if (hasMeta && hasMeta.getCellContext) {
                        className = hasMeta.getCellContext(cell.getContext()).className;
                      }

                      const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());
                      const shouldWrapInLink = rowUrl && !meta?.isInteractive;

                      return (
                        <TableCell
                          key={cell.id}
                          style={{
                            width: meta?.width ? meta.width : "auto",
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                          }}
                          className={className}
                        >
                          {shouldWrapInLink ? (
                            <Link to={rowUrl} className="-m-1 block p-1">
                              {cellContent}
                            </Link>
                          ) : (
                            cellContent
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length}>Pas de résultats.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
